/** Тексты нумерологии для пар чисел жизненного пути (9×9). */

type NumerologyPairCopy = {
  positive: string;
  negative: string;
  tip: string;
};

const LIFE_PATH_TRAITS: Record<number, { role: string; strength: string; shadow: string }> = {
  1: { role: "лидер", strength: "инициативу и самостоятельность", shadow: "упрямство и желание доминировать" },
  2: { role: "дипломат", strength: "чуткость и умение сглаживать", shadow: "зависимость от одобрения и обиды" },
  3: { role: "творец", strength: "лёгкость, юмор и самовыражение", shadow: "поверхностность и разбросанность" },
  4: { role: "строитель", strength: "надёжность и системность", shadow: "жёсткость и страх перемен" },
  5: { role: "исследователь", strength: "свободу и любознательность", shadow: "беспокойство и непостоянство" },
  6: { role: "хранитель", strength: "заботу и ответственность", shadow: "контроль и жертвенность" },
  7: { role: "мыслитель", strength: "глубину и самоанализ", shadow: "отстранённость и недоверие" },
  8: { role: "реализатор", strength: "целеустремлённость и энергию результата", shadow: "работу важнее чувств" },
  9: { role: "гуманист", strength: "эмпатию и широту взгляда", shadow: "идеализм и уход от бытовых тем" },
};

const LIFE_PATH_COMPAT: Record<number, number[]> = {
  1: [1, 5, 7],
  2: [2, 4, 8],
  3: [3, 6, 9],
  4: [2, 4, 8],
  5: [1, 5, 7],
  6: [3, 6, 9],
  7: [1, 5, 7],
  8: [2, 4, 8],
  9: [3, 6, 9],
};

function buildNumerologyPair(a: number, b: number): NumerologyPairCopy {
  const ta = LIFE_PATH_TRAITS[a] ?? LIFE_PATH_TRAITS[1];
  const tb = LIFE_PATH_TRAITS[b] ?? LIFE_PATH_TRAITS[1];
  const harmonious = (LIFE_PATH_COMPAT[a] ?? []).includes(b);
  const same = a === b;

  if (same) {
    return {
      positive: `Оба с числом ${a} (${ta.role}): вы одинаково цените ${ta.strength} и быстро понимаете мотивы друг друга.`,
      negative: `Повторяются и слабые стороны числа ${a}: ${ta.shadow}. В паре не хватает контраста.`,
      tip: "Один из вас пусть берёт на себя «внешний» круг, другой — «внутренний» быт, чтобы не дублировать одну роль.",
    };
  }

  if (harmonious) {
    return {
      positive: `Число ${a} (${ta.role}) и число ${b} (${tb.role}) хорошо сочетаются: ${ta.strength} поддерживает ${tb.strength}.`,
      negative: `Трение возможно там, где ${ta.shadow} встречается с ${tb.shadow}.`,
      tip: "Опирайтесь на сильные стороны чисел и заранее делите зоны ответственности — так меньше споров «кто как должен».",
    };
  }

  return {
    positive: `Числа ${a} и ${b} разные по природе, но это даёт баланс: ${ta.role} и ${tb.role} закрывают разные потребности пары.`,
    negative: `Риск недопонимания: ${ta.shadow} может конфликтовать с ${tb.shadow}. Разный ритм жизни и приоритеты.`,
    tip: "Проговаривайте ожидания конкретно — не «будь внимательнее», а «мне важно сообщение, если опаздываешь».",
  };
}

const NUMEROLOGY_PAIR_TEXTS: Record<number, Record<number, NumerologyPairCopy>> = {};

for (let a = 1; a <= 9; a += 1) {
  NUMEROLOGY_PAIR_TEXTS[a] = {};
  for (let b = 1; b <= 9; b += 1) {
    NUMEROLOGY_PAIR_TEXTS[a][b] = buildNumerologyPair(a, b);
  }
}

const NUMEROLOGY_OVERRIDES: [number, number, NumerologyPairCopy][] = [
  [1, 8, {
    positive: "Лидер (1) и реализатор (8) — мощный союз для амбиций, проектов и материального роста.",
    negative: "Оба сильные: борьба за контроль и мало места для уязвимости.",
    tip: "Отдельно обсудите, где вы партнёры, а где — союзники в деле, не конкуренты.",
  }],
  [2, 7, {
    positive: "Чуткость двойки и глубина семёрки — тонкая эмоциональная связь при взаимном доверии.",
    negative: "Семёрка уходит в себя, двойка ждёт отклика — возможны обиды без слов.",
    tip: "Договоритесь о «сигнале»: когда нужно побыть одному, это не отвержение.",
  }],
  [3, 5, {
    positive: "Тройка и пятёрка — лёгкость, путешествия, смех. Пара не застаивается.",
    negative: "Сложно с рутиной и долгими обязательствами — всё «на энтузиазме».",
    tip: "Автоплатёж, общий список дел и одна «скучная» привычка в месяц спасают от хаоса.",
  }],
  [4, 9, {
    positive: "Четвёрка даёт опору, девятка — смысл и тепло. Хорошо для семьи и долгих планов.",
    negative: "Четвёрке нужен порядок, девятке — идеалы; быт vs мечты.",
    tip: "Один список целей на год: и практичные (4), и «для души» (9).",
  }],
  [6, 8, {
    positive: "Шестёрка заботится о доме, восьмёрка — о ресурсах. Сильная «семейно‑деловая» связка.",
    negative: "Шестёрка может контролировать, восьмёрка — работать без отдыха. Выгорание пары.",
    tip: "В календаре — обязательные выходные без задач, не только «когда получится».",
  }],
];

for (const [a, b, copy] of NUMEROLOGY_OVERRIDES) {
  NUMEROLOGY_PAIR_TEXTS[a][b] = copy;
}

export function getNumerologyPairCopy(a: number, b: number): NumerologyPairCopy {
  const x = Math.min(9, Math.max(1, a));
  const y = Math.min(9, Math.max(1, b));
  return NUMEROLOGY_PAIR_TEXTS[x][y];
}
